import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Refresh from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCallback, useState } from "react";

import { useCalendarSubscriptionUrl } from "./useCalendarSubscriptionUrl";

type TaskCalendarSubscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const TaskCalendarSubscriptionDialog: React.FC<TaskCalendarSubscriptionDialogProps> = ({
  open,
  onClose,
}) => {
  const alerts = useAlerts();
  const [copied, setCopied] = useState(false);
  const copyToClipboard = useCopyToClipboard();

  const { subscriptionUrl, loading, generating, revoking, generateUrl, revokeUrl } =
    useCalendarSubscriptionUrl({
      skip: !open,
    });
  const isMutating = generating || revoking;

  const handleCopy = useCallback(async () => {
    if (subscriptionUrl) {
      try {
        await copyToClipboard(subscriptionUrl);
        setCopied(true);
        alerts.success("URL copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        alerts.error("Failed to copy URL");
      }
    }
  }, [subscriptionUrl, alerts, copyToClipboard]);

  const handleGenerate = useCallback(async () => {
    try {
      await generateUrl();
      alerts.success("Calendar subscription URL generated");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      alerts.error(`Failed to generate URL: ${message}`);
    }
  }, [alerts, generateUrl]);

  const handleRevoke = useCallback(async () => {
    try {
      const revoked = await revokeUrl();
      if (revoked) {
        alerts.success("Calendar subscription revoked");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      alerts.error(`Failed to revoke: ${message}`);
    }
  }, [alerts, revokeUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      title={
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarMonth color="primary" />
          <Typography variant="h6">Calendar Subscription</Typography>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          Subscribe to your tasks in your favorite calendar app (Google Calendar, Apple Calendar,
          Outlook, etc.). Your calendar will automatically sync with your tasks in Encamp.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        ) : subscriptionUrl ? (
          <>
            <TextField
              label="Subscription URL"
              value={subscriptionUrl}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                      <IconButton onClick={handleCopy} edge="end">
                        <ContentCopy color={copied ? "success" : "inherit"} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                },
              }}
            />

            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                <strong>How to use:</strong> Copy this URL and add it as a calendar subscription in
                your calendar app. Changes may take up to 24 hours to appear (depending on your
                calendar app).
              </Typography>
            </Alert>

            <Stack direction="row" spacing={2}>
              <Tooltip title="Generate a new URL (this will invalidate the current one)">
                <Button
                  variant="outlined"
                  startIcon={
                    generating ? <CircularProgress size={16} color="inherit" /> : <Refresh />
                  }
                  onClick={handleGenerate}
                  disabled={isMutating}
                >
                  Regenerate URL
                </Button>
              </Tooltip>
            </Stack>
          </>
        ) : (
          <>
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2">
                You haven't set up a calendar subscription yet. Generate a URL to get started.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              startIcon={
                generating ? <CircularProgress size={16} color="inherit" /> : <CalendarMonth />
              }
              onClick={handleGenerate}
              disabled={isMutating}
            >
              Generate Subscription URL
            </Button>
          </>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">
            <strong>Privacy note:</strong> This URL contains a private token. Anyone with this URL
            can see your tasks. If you believe your URL has been compromised, regenerate it to
            invalidate the old one.
          </Typography>
        </Box>

        {subscriptionUrl && (
          <Box>
            <Link
              component="button"
              variant="body2"
              color="error"
              onClick={handleRevoke}
              disabled={isMutating}
              sx={{ cursor: isMutating ? "default" : "pointer" }}
            >
              Revoke calendar access
            </Link>
          </Box>
        )}
      </Stack>
    </Dialog>
  );
};
